import React, { useEffect, useMemo, useContext, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    Typography,
    Avatar,
} from "@mui/material";
import { AppContext } from "../../../AppContext";
import { fDateTime, fToNow } from "../../../utils/formatTime";
import EntryItemRow from "./EntryItemRow";
import AddonsRow from "./AddonsRow";
import { groupItemsByMinute, parseDateSafe } from "../utils/grouping";
import { useStyles } from "../styles";

function getItemTimestamp(item) {
    return parseDateSafe(item.ordered_time) || parseDateSafe(item.creation);
}

const OrderCard = ({
    order,
    isMutating,
    selectedStatuses,
    selectedCenters,
    canAdvanceStatus,
    onAdvanceIdentifiers,
    onAdvanceItem,
    selected,
    onToggleSelect,
    onExpand,
    highlight,
    highlightItemIds,
}) => {
    const classes = useStyles();
    const { menuItems, getItem } = useContext(AppContext);

    // Re-render every minute so relative times (fToNow) stay fresh
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    // Build groups: if multiple statuses selected, group by status first in selection order
    const grouped = useMemo(() => {
        const items = order.entry_items || [];
        if (Array.isArray(selectedStatuses) && selectedStatuses.length > 1) {
            const byStatus = [];
            selectedStatuses.forEach((status) => {
                const statusItems = items.filter((i) => i.status === status);
                if (statusItems.length > 0) {
                    byStatus.push({
                        status,
                        groups: groupItemsByMinute(statusItems),
                    });
                }
            });
            return byStatus;
        }
        // Single status or none: treat as one block without an outer status group
        return [
            {
                status: items[0]?.status || null,
                groups: groupItemsByMinute(items),
            },
        ];
    }, [order, selectedStatuses]);

    const createdAt = parseDateSafe(order.creation);
    const updatedAt = parseDateSafe(order.modified);

    return (
        <Card elevation={1} className={`${classes.OrderCardRoot} ${highlight ? classes.HighlightCard : ""}`}>
            <CardContent>
                <Box className={classes.OrderCardHeader}>
                    <Box
                        className="left"
                        onClick={() => onExpand?.(order)}
                        style={{ cursor: "pointer" }}
                    >
                        <Typography variant="subtitle1" className="title">
                            {order.table_description || "Order"}
                        </Typography>
                        <Typography variant="caption" className="subtitle">
                            {order.name}
                        </Typography>
                    </Box>
                    <Box className="right">
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onExpand?.(order)}
                            sx={{ mr: 1 }}
                        >
                            Expand
                        </Button>
                        <Button
                            size="small"
                            variant={selected ? "contained" : "outlined"}
                            onClick={() => onToggleSelect?.(order)}
                        >
                            {selected ? "Selected" : "Select"}
                        </Button>
                    </Box>
                </Box>

                {order.notes && String(order.notes).trim().length > 0 && (
                    <Typography variant="body2" className={classes.OrderNotes}>
                        {order.notes}
                    </Typography>
                )}

                <Box className={classes.OrderMeta}>
                    {createdAt && (
                        <Typography variant="caption" className="created">
                            Created: {fDateTime(createdAt)}
                        </Typography>
                    )}
                </Box>

                <Divider className={classes.CardDivider} />

                <Stack spacing={2}>
                    {grouped.map((statusBlock, sIdx) => (
                        <Box key={(statusBlock.status || "nostatus") + sIdx}>
                            {selectedStatuses.length > 1 && (
                                <Typography
                                    variant="subtitle2"
                                    className={classes.StatusHeader}
                                >
                                    {statusBlock.status}
                                </Typography>
                            )}
                            <Stack spacing={1.5}>
                        {statusBlock.groups.map((group) => {
                            // Compute header time from earliest status_change_date in this group
                            let headerTime = null;
                            (group.items || []).forEach((it) => {
                                const d = parseDateSafe(it.status_change_date);
                                if (d && (!headerTime || d < headerTime)) headerTime = d;
                            });
                            if (!headerTime) headerTime = group.time;
                            return (
                            <Box key={group.key}>
                                <Box className={classes.GroupHeaderRow}>
                                    <Typography variant="overline" className={classes.GroupHeader}>
                                        {headerTime ? fToNow(headerTime) : ""}
                                    </Typography>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={
                                                    isMutating ||
                                                    !canAdvanceStatus(
                                                        statusBlock.status
                                                    )
                                                }
                                                onClick={() => {
                                                    const ids = group.items
                                                        .filter(
                                                            (i) => i.identifier
                                                        )
                                                        .map(
                                                            (i) => i.identifier
                                                        );
                                                    onAdvanceIdentifiers(
                                                        order,
                                                        ids
                                                    );
                                                }}
                                            >
                                                Done All
                                            </Button>
                                        </Box>
                                        <Stack
                                            spacing={1}
                                            className={classes.GroupItems}
                                        >
                                            {(() => {
                                                const items = group.items || [];
                                                const byId = new Map(
                                                    items.map((it) => [
                                                        it.identifier,
                                                        it,
                                                    ])
                                                );
                                                const childrenByParent =
                                                    new Map();
                                                items.forEach((it) => {
                                                    const p = it.parent_item;
                                                    if (
                                                        p &&
                                                        p !== "NULL" &&
                                                        byId.has(p)
                                                    ) {
                                                        if (
                                                            !childrenByParent.has(
                                                                p
                                                            )
                                                        )
                                                            childrenByParent.set(
                                                                p,
                                                                []
                                                            );
                                                        childrenByParent
                                                            .get(p)
                                                            .push(it);
                                                    }
                                                });
                                                const parents = items.filter(
                                                    (it) =>
                                                        !it.parent_item ||
                                                        it.parent_item ===
                                                            "NULL" ||
                                                        !byId.has(
                                                            it.parent_item
                                                        )
                                                );

                                                // row rendering moved to EntryItemRow

                                                return parents.map((parent) => (
                                                    <Box
                                                        key={
                                                            (parent.name ||
                                                                "") +
                                                            parent.identifier +
                                                            "-parent"
                                                        }
                                                        style={{
                                                            marginTop: 8,
                                                        }}
                                                    >
                                                        <EntryItemRow
                                                            item={parent}
                                                            order={order}
                                                            isAddon={false}
                                                            group={group}
                                                            canAdvanceStatus={canAdvanceStatus}
                                                            isMutating={isMutating}
                                                            onAdvanceItem={onAdvanceItem}
                                                            getItem={getItem}
                                                            selectedCenters={selectedCenters}
                                                            classes={classes}
                                                            highlight={highlightItemIds && parent.identifier && highlightItemIds.has(parent.identifier)}
                                                        />
                                                        {childrenByParent.has(
                                                            parent.identifier
                                                        ) && (
                                                            <AddonsRow
                                                                addons={childrenByParent.get(parent.identifier)}
                                                                getItem={getItem}
                                                                classes={classes}
                                                            />
                                                        )}
                                                    </Box>
                                                ));
                                            })()}
                                        </Stack>
                                    </Box>
                                );
                                })}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default OrderCard;
